import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

interface ThemeStore {
  mode: ThemeMode
  resolvedTheme: ResolvedTheme
  setMode: (mode: ThemeMode) => void
  toggleTheme: () => void
  syncResolvedTheme: () => void
}

const prefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-color-scheme: dark)').matches

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') return prefersDark() ? 'dark' : 'light'
  return mode
}

export function applyTheme(theme: ResolvedTheme) {
  if (typeof document === 'undefined') return
  document.documentElement.dataset.theme = theme
  document.documentElement.style.colorScheme = theme
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      mode: 'dark',
      resolvedTheme: 'dark',

      setMode: (mode) => {
        const resolvedTheme = resolveTheme(mode)
        applyTheme(resolvedTheme)
        set({ mode, resolvedTheme })
      },

      toggleTheme: () => {
        const next = get().resolvedTheme === 'dark' ? 'light' : 'dark'
        applyTheme(next)
        set({ mode: next, resolvedTheme: next })
      },

      syncResolvedTheme: () => {
        const resolvedTheme = resolveTheme(get().mode)
        applyTheme(resolvedTheme)
        set({ resolvedTheme })
      },
    }),
    {
      name: 'theme-store',
      partialize: (state) => ({ mode: state.mode }),
      onRehydrateStorage: () => (state) => {
        state?.syncResolvedTheme()
      },
    },
  ),
)

export function initializeTheme() {
  const state = useThemeStore.getState()
  const resolvedTheme = resolveTheme(state.mode)
  applyTheme(resolvedTheme)
  useThemeStore.setState({ resolvedTheme })
}
